import type { ReactNode } from 'react';
import { Button } from '../../design-system/Button';
import { SleekButton, useIsSleek } from '../../theme';

const SLEEK_TEXT_BUTTON =
  'inline-flex h-11 w-full items-center justify-center gap-2 text-[16px] font-semibold tracking-[-0.31px] ' +
  'text-[var(--co-FairnessSheet-text,#FFFFFF)] transition-opacity hover:opacity-80 disabled:opacity-50';

/** The sheet's closing button: sleek's outlined DONE, the palette primary elsewhere. */
export function FairnessPrimaryButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  if (useIsSleek()) {
    return (
      <SleekButton name="FairnessDoneButton" onClick={onClick}>
        {children}
      </SleekButton>
    );
  }
  return (
    <Button variant="primary" size="lg" fullWidth onClick={onClick}>
      {children}
    </Button>
  );
}

export interface FairnessActionProps {
  icon?: ReactNode;
  children: ReactNode;
  /** Opens in a new tab instead of running `onClick`. */
  href?: string;
  onClick?: () => void;
  loading?: boolean;
}

/** A secondary action under the content: sleek's plain text row with an icon, a secondary button (or link) elsewhere. */
export function FairnessAction({ icon, children, href, onClick, loading = false }: FairnessActionProps) {
  const sleek = useIsSleek();
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={
          sleek
            ? SLEEK_TEXT_BUTTON
            : 'inline-flex h-12 w-full items-center justify-center gap-2 text-[14px] font-semibold underline underline-offset-2'
        }
        style={sleek ? undefined : { color: 'var(--ui-text, #15181E)' }}
      >
        {icon}
        {children}
      </a>
    );
  }
  if (sleek) {
    return (
      <button type="button" className={SLEEK_TEXT_BUTTON} onClick={onClick} disabled={loading}>
        {icon}
        {children}
      </button>
    );
  }
  return (
    <Button variant="secondary" size="lg" fullWidth loading={loading} {...(onClick ? { onClick } : {})}>
      {children}
    </Button>
  );
}
